import React from "react";
import styled, { ThemeProvider, css } from "styled-components";
import PropTypes from "prop-types";
import { space, layout, color, background, border, compose, variant } from "styled-system";
import shouldForwardProp from "@styled-system/should-forward-prop";
import Icon from "../Icon";
import Loading from "../Loading";
import { themeGet } from "@styled-system/theme-get";
import { Link } from "react-router-dom";
import { omit } from "lodash";

// Custom shouldForwardProp that also filters out borderRadius and borderColor
const customShouldForwardProp = (prop) => {
  // Filter out styled-system props that shouldn't go to DOM
  if (["borderRadius", "borderColor"].includes(prop)) {
    return false;
  }
  return shouldForwardProp(prop);
};

const getOutlineStyle = (color) => css`
  outline: 0;
  box-shadow: ${(props) =>
    [themeGet("shadows.thinOutline")(props), themeGet(color)(props)].join(" ")};
`;

const getVariantStyles = (props) => {
  // Handle disabled state - override variant if disabled
  if (props.disabled) {
    return css`
      background: ${themeGet("colors.greyLighter")(props)};
      color: ${themeGet("colors.grey")(props)};
      border-color: ${themeGet("colors.greyLighter")(props)};
      &:hover {
        background: ${themeGet("colors.greyLighter")(props)};
        color: ${themeGet("colors.grey")(props)};
        border-color: ${themeGet("colors.greyLighter")(props)};
      }
    `;
  }

  // Special handling for AI variant with gradient
  if (props.variant === "ai") {
    return css`
      background: linear-gradient(
        135deg,
        ${themeGet("colors.primaryLight")} 0%,
        ${themeGet("colors.secondaryLight")} 100%
      );
      color: ${themeGet("colors.white")};
      border-color: ${themeGet("colors.secondaryLight")};

      &:hover {
        background: linear-gradient(
          135deg,
          ${themeGet("colors.primary")} 0%,
          ${themeGet("colors.secondary")} 100%
        );
        color: ${themeGet("colors.white")};
        border-color: ${themeGet("colors.secondary")};
      }

      &:focus {
        ${getOutlineStyle("colors.secondary")}
      }
    `;
  }

  return variant({
    variants: {
      default: {
        background: themeGet("colors.primary")(props),
        color: themeGet("colors.white")(props),
        borderColor: themeGet("colors.primary")(props),
        "&:hover": {
          background: themeGet("colors.primaryDark")(props),
          color: themeGet("colors.white")(props),
          borderColor: themeGet("colors.primaryDark")(props)
        },
        "&:focus": {
          outline: 0,
          boxShadow: `${themeGet("shadows.thinOutline")(props)} ${themeGet("colors.primaryDarker")(
            props
          )}`
        }
      },
      success: {
        background: themeGet("colors.success")(props),
        color: themeGet("colors.white")(props),
        borderColor: themeGet("colors.success")(props),
        "&:hover": {
          background: themeGet("colors.successDark")(props),
          color: themeGet("colors.white")(props),
          borderColor: themeGet("colors.successDark")(props)
        },
        "&:focus": {
          outline: 0,
          boxShadow: `${themeGet("shadows.thinOutline")(props)} ${themeGet("colors.successDarker")(
            props
          )}`
        }
      },
      successAlternate: {
        background: themeGet("colors.greyLightest")(props),
        color: themeGet("colors.success")(props),
        borderColor: themeGet("colors.greyLightest")(props),
        "&:hover": {
          background: themeGet("colors.greyLighter")(props),
          color: themeGet("colors.success")(props),
          borderColor: themeGet("colors.greyLighter")(props)
        },
        "&:focus": {
          outline: 0,
          boxShadow: `${themeGet("shadows.thinOutline")(props)} ${themeGet("colors.successLight")(
            props
          )}`
        }
      },
      danger: {
        background: themeGet("colors.danger")(props),
        color: themeGet("colors.white")(props),
        borderColor: themeGet("colors.danger")(props),
        "&:hover": {
          background: themeGet("colors.dangerDark")(props),
          color: themeGet("colors.white")(props),
          borderColor: themeGet("colors.dangerDark")(props)
        },
        "&:focus": {
          outline: 0,
          boxShadow: `${themeGet("shadows.thinOutline")(props)} ${themeGet("colors.dangerDarker")(
            props
          )}`
        }
      },
      dangerAlternate: {
        background: themeGet("colors.greyLightest")(props),
        color: themeGet("colors.danger")(props),
        borderColor: themeGet("colors.greyLightest")(props),
        "&:hover": {
          background: themeGet("colors.greyLighter")(props),
          color: themeGet("colors.danger")(props),
          borderColor: themeGet("colors.greyLighter")(props)
        },
        "&:focus": {
          outline: 0,
          boxShadow: `${themeGet("shadows.thinOutline")(props)} ${themeGet("colors.dangerLight")(
            props
          )}`
        }
      },
      ghost: {
        background: themeGet("colors.primaryLightest")(props),
        color: themeGet("colors.primaryDark")(props),
        borderColor: themeGet("colors.primaryLightest")(props),
        "&:hover": {
          background: themeGet("colors.primaryLighter")(props),
          color: themeGet("colors.primaryDarker")(props),
          borderColor: themeGet("colors.primaryLighter")(props)
        },
        "&:focus": {
          outline: 0,
          boxShadow: `${themeGet("shadows.thinOutline")(props)} ${themeGet("colors.primaryLight")(
            props
          )}`
        }
      },
      borderBlue: {
        background: themeGet("colors.white")(props),
        color: themeGet("colors.primary")(props),
        borderColor: themeGet("colors.primary")(props),
        "&:hover": {
          background: themeGet("colors.primaryLightest")(props),
          color: themeGet("colors.primaryDark")(props),
          borderColor: themeGet("colors.primaryDark")(props)
        },
        "&:focus": {
          outline: 0,
          boxShadow: `${themeGet("shadows.thinOutline")(props)} ${themeGet("colors.primaryDark")(
            props
          )}`
        }
      },
      borderGrey: {
        background: themeGet("colors.white")(props),
        color: themeGet("colors.greyDarkest")(props),
        borderColor: themeGet("colors.greyDarkest")(props),
        "&:hover": {
          background: themeGet("colors.primaryLightest")(props),
          color: themeGet("colors.greyDarkest")(props),
          borderColor: themeGet("colors.greyDarkest")(props)
        },
        "&:focus": {
          outline: 0,
          boxShadow: `${themeGet("shadows.thinOutline")(props)} ${themeGet("colors.greyDarkest")(
            props
          )}`
        }
      },
      disabled: {
        background: themeGet("colors.greyLighter")(props),
        color: themeGet("colors.grey")(props),
        borderColor: themeGet("colors.greyLighter")(props),
        "&:hover": {
          background: themeGet("colors.greyLighter")(props),
          color: themeGet("colors.grey")(props),
          borderColor: themeGet("colors.greyLighter")(props)
        }
      }
    }
  })(props);
};

const getSpace = (getter) => (props) => themeGet(`space.${getter(props)}`)(props);

const buttonStyles = css`
  background: ${themeGet("colors.primary")};
  color: ${themeGet("colors.white")};
  border-color: ${themeGet("colors.primary")};
  display: flex;
  align-items: center;
  justify-content: center;
  appearance: none;
  box-shadow: none;
  margin: 0;
  text-decoration: none;
  text-align: center;
  font-family: ${themeGet("fonts.main")};
  font-weight: ${themeGet("fontWeights.2")};
  border-radius: ${themeGet("radii.2")};
  transition: ${themeGet("transition.transitionDefault")};
  border-width: ${themeGet("borderWidths.1")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : props.isLoading ? "progress" : "pointer")};
  width: ${(props) => (props.fullWidth ? "100%" : "auto")};
  height: auto;
  font-size: ${(props) => {
    let fontSize = 2;

    if (props.large && props.iconOnly) fontSize = 5;
    if (props.large) fontSize = 3;
    if (props.small) fontSize = 1;

    return themeGet(`fontSizes.${fontSize}`)(props);
  }};

  padding: ${getSpace((props) => (props.large ? "s" : props.small ? "xxs" : "xs"))}
    ${getSpace((props) => (props.large ? "r" : props.small ? "s" : "between"))};

  svg {
    margin-right: ${getSpace((props) => (!props.iconLeft ? "" : props.small ? "xs" : "s"))};
    margin-left: ${getSpace((props) => (!props.iconRight ? "" : props.small ? "xs" : "s"))};
  }
  &:hover {
    background: ${themeGet("colors.primaryDark")};
    border-color: ${themeGet("colors.primaryDark")};
    border-width: ${themeGet("borderWidths.1")};
    border-style: solid;
  }
  &:focus {
    outline: 0;
    box-shadow: ${themeGet("shadows.thinOutline")} ${themeGet("colors.primaryDarker")};
  }

  ${getVariantStyles}
  ${compose(space, layout, color, background, border)}
`;

const attrs = (props) => ({
  "data-testid": props.dataTestId || props["data-testid"],
  disabled: props.disabled || props.variant == "disabled",
  className: `${props.className || ""} variant-${props.variant || "default"}`
});

const StyledButton = styled("button")
  .withConfig({ shouldForwardProp: customShouldForwardProp })
  .attrs(attrs)`
    ${buttonStyles}
  `;

const linkStyles = css`
  width: ${(props) => props.width || "fit-content"};
  display: ${(props) => (props.height || props.width ? "flex" : props.display || "inline-block")};
  align-items: ${(props) => props.alignItems || "center"};
`;

const StyledButtonLink = styled.a
  .withConfig({ shouldForwardProp: customShouldForwardProp })
  .attrs(attrs)`
    ${buttonStyles}
    ${linkStyles}
  `;

const StyledReactButtonLink = styled(Link)
  .withConfig({ shouldForwardProp: customShouldForwardProp })
  .attrs(attrs)`
    ${buttonStyles}
    ${linkStyles}
  `;

const buttonPropTypes = {
  /** Large button */
  large: PropTypes.bool,
  /** Small button */
  small: PropTypes.bool,
  /** Specifies alternate button colours/styles. */
  variant: PropTypes.oneOf([
    "success",
    "successAlternate",
    "danger",
    "dangerAlternate",
    "ghost",
    "disabled",
    "default",
    "ai",
    "borderBlue",
    "borderGrey"
  ]),
  /** Full width button that takes up all available space of parent */
  fullWidth: PropTypes.bool,
  /** Adds a spinner animation to indicate loading or processing */
  isLoading: PropTypes.bool,
  /** Styles button to fit an icon on the left of text. Uses Icon component. */
  iconLeft: PropTypes.bool,
  /** Styles button to fit an icon on the right of text. Uses Icon component. */
  iconRight: PropTypes.bool,
  /** New functionality to specify an `Icon` on the left side without having to include it as a child. */
  leftIcon: PropTypes.array,
  /** New functionality to specify an `Icon` on the right side without having to include it as a child. */
  rightIcon: PropTypes.array,
  /** Styles button to suit having only an icon. Uses Icon component. */
  iconOnly: PropTypes.bool,
  /** Specifies whether the button is disabled. */
  disabled: PropTypes.bool,
  /** The text label on the button is passed as a child. Keep this text short and descriptive. Use an action word or confirmation if possible. */
  children: PropTypes.node,
  /** Adds additional styling to the rendered `<button>` using `space`, `layout`, `color` and `border` prop categories */
  ButtonStyles: PropTypes.object,
  /** Specifies the `data-testid` attribute for testing. */
  dataTestId: PropTypes.string,
  /** Specifies aria-label for iconOnly buttons. This is only required if the iconOnly button is used, as it doesn't have supporting text for accessibility.*/
  ariaLabel: (props, propName) => {
    if (props.iconOnly && (props[propName] == null || props[propName] === "")) {
      return new Error(
        `Missing prop \`${propName}\` not specified for Button component. When \`iconOnly\` is true, \`${propName}\` is required.`
      );
    }
    if (props[propName] && typeof props[propName] !== "string") {
      return new Error(
        `Invalid propType for \`${propName}\` supplied to Button component. Expected \`string\`, received \`${typeof props[
          propName
        ]}\`.`
      );
    }
    return null;
  },
  /** Specifies the color theme object. */
  theme: PropTypes.object
};

// Export variant colors for use in stories and other components
export const VARIANT_COLORS = {
  default: {
    color: "colors.white",
    background: "colors.primary",
    hovered: {
      color: "colors.white",
      background: "colors.primaryDark"
    }
  },
  success: {
    color: "colors.white",
    background: "colors.success",
    hovered: {
      color: "colors.white",
      background: "colors.successDark"
    }
  },
  successAlternate: {
    color: "colors.success",
    background: "colors.greyLightest",
    hovered: {
      color: "colors.success",
      background: "colors.greyLighter"
    }
  },
  danger: {
    color: "colors.white",
    background: "colors.danger",
    hovered: {
      color: "colors.white",
      background: "colors.dangerDark"
    }
  },
  dangerAlternate: {
    color: "colors.danger",
    background: "colors.greyLightest",
    hovered: {
      color: "colors.danger",
      background: "colors.greyLighter"
    }
  },
  ghost: {
    color: "colors.primaryDark",
    background: "colors.primaryLightest",
    hovered: {
      color: "colors.primaryDarker",
      background: "colors.primaryLighter"
    }
  },
  disabled: {
    color: "colors.grey",
    background: "colors.greyLighter",
    hovered: {
      color: "colors.grey",
      background: "colors.greyLighter"
    }
  },
  ai: {
    color: "colors.white",
    background: "colors.primaryLight",
    hovered: {
      color: "colors.white",
      background: "colors.primary"
    }
  },
  borderBlue: {
    color: "colors.primary",
    background: "colors.white",
    hovered: {
      color: "colors.primaryDark",
      background: "colors.primaryLightest"
    }
  },
  borderGrey: {
    color: "colors.greyDarkest",
    background: "colors.white",
    hovered: {
      color: "colors.greyDarkest",
      background: "colors.primaryLightest"
    }
  }
};

const renderButton = (ButtonComponent, { ariaLabel, variant = "default", ...props }) => (
  <ButtonComponent
    variant={variant}
    borderStyle="solid"
    {...props.ButtonStyles}
    {...props}
    aria-label={ariaLabel}
    ref={props.ref}
  >
    {props.leftIcon && <Icon icon={props.leftIcon} mr={props.small ? "xxs" : "xs"} />}
    {props.children}
    {props.rightIcon && <Icon icon={props.rightIcon} ml={props.small ? "xxs" : "xs"} />}
    {props.isLoading && <Loading inverted ml="s" />}
  </ButtonComponent>
);

export const ButtonLink = React.forwardRef((props, ref) => {
  const { theme } = props;

  const component = renderButton(props.to ? StyledReactButtonLink : StyledButtonLink, {
    ...omit(props, "isLoading"),
    ref
  });

  return theme ? <ThemeProvider theme={theme}>{component}</ThemeProvider> : component;
});

ButtonLink.propTypes = {
  ...buttonPropTypes,
  target: PropTypes.string,
  /** Link to navigate user to */
  href: PropTypes.string
};

export const Button = React.forwardRef((props, ref) => {
  const { theme } = props;

  const component = renderButton(StyledButton, { ...props, ref });

  return theme ? <ThemeProvider theme={theme}>{component}</ThemeProvider> : component;
});

export default Button;

Button.propTypes = {
  ...buttonPropTypes,
  /** Function to run when the `Button` is clicked */
  onClick: PropTypes.func
};
